import {
  TTranslation,
  useDynamicOptionsBuilder,
} from "@/features/FormCard/hooks";
import { TSections } from "@/features/FormCard/types";
import { useEffect, useRef, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { MovementsBySection, DEFAULT_MOVEMENTS_BY_SECTION } from "./config";
import {
  getMovementsBySections,
  isCashFormat,
  isCashAccount,
  shouldAutoSelectOption,
  shouldResetCategoryValue,
  AccountType,
} from "./utils";
import type { EditFormSchemaFields } from "./edit_card";
import type { NewFormSchemaFields } from "./new";

type FormFields = Pick<
  NewFormSchemaFields,
  Extract<keyof NewFormSchemaFields, keyof EditFormSchemaFields>
>;


/**
 * Hook to manage category field state based on asset class and account type
 *
 * @param form Form instance
 * @returns State for category field (disabled state)
 */
export function useCategoryFieldState(
  formFields: FormFields,
  form: UseFormReturn<any>,
  isLoadingData: boolean,
) {
  const [isCategoryDisabled, setIsCategoryDisabled] = useState(false);

  const prevAssetClass2Ref = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isLoadingData) return;

    const assetClass2 = form.watch(
      formFields.n1_static_datum_name,
    )?.asset_class_2;

    // Disable category field if no asset class selected
    if (!assetClass2) {
      setIsCategoryDisabled(true);
      return;
    }

    // Enable category for all other cases
    setIsCategoryDisabled(false);

    if (shouldResetCategoryValue(prevAssetClass2Ref.current, assetClass2)) {
      form.setValue(formFields.category, null, { shouldDirty: true });
    }

    prevAssetClass2Ref.current = assetClass2;
  }, [
    form.watch(formFields.n1_static_datum_name)?.asset_class_2,
    form.watch(formFields.n1_portfolio_name)?.account_type,
    form,
    isLoadingData,
  ]);

  return { isCategoryDisabled };
}

/**
 * Hook to handle automatic selection of the single option
 *
 * @param options Available options
 * @param form Form instance
 * @param isCategoryDisabled If the category field is disabled
 * @param translations Optional translations for category values
 */
export function useAutoSelectSingleOption(
  formFields: FormFields,
  options: TSections,
  form: UseFormReturn<any>,
  isCategoryDisabled: boolean,
  translations?: TTranslation,
  keysAllowNullValue: string[] = [],
) {
  useEffect(() => {
    const currentCategory = form.watch(formFields.category) || "";
    const accountType = form.watch(formFields.n1_portfolio_name)?.account_type;
    const staticDatumName = form.watch(formFields.n1_static_datum_name)?.text;

    if (currentCategory) return;
    if (isCategoryDisabled)
      return form.setValue(formFields.category, null, { shouldDirty: true });

    if (isCashAccount(accountType) && isCashFormat(staticDatumName)) {
      return form.setValue(formFields.category, null, { shouldDirty: true });
    }

    const { shouldSelect, selectedValue } = shouldAutoSelectOption(
      options,
      currentCategory,
      isCategoryDisabled,
      keysAllowNullValue,
    );

    if (shouldSelect && selectedValue) {
      form.setValue(formFields.category, selectedValue, { shouldDirty: true });
    }
  }, [options, isCategoryDisabled, form, translations]);
}

/**
 * Hook to filter category options based on asset classes and account type
 *
 * @param form Form instance
 * @param translations Translations for options
 * @returns Filtered category options
 */
export function getMovementsSections(
  formFields: FormFields,
  form: UseFormReturn<FieldValues>,
  translations?: TTranslation,
): TSections {
  const category_options = useDynamicOptionsBuilder({
    translation: translations?.[formFields.category],
    options: DEFAULT_MOVEMENTS_BY_SECTION,
    sectionNameTranslationPrefix: "transactions.category_groups",
    adjustOptions: (movementsBySections) => {
      const assetClass2 = form.watch(
        formFields.n1_static_datum_name,
      )?.asset_class_2;
      const accountType = form.watch(
        formFields.n1_portfolio_name,
      )?.account_type;
      const staticDatumName = form.watch(formFields.n1_static_datum_name)?.text;
      const marketValueLocalCcy = form.watch(formFields.market_value_local_ccy);

      return getMovementsBySections(
        movementsBySections as MovementsBySection[],
        accountType as AccountType,
        assetClass2,
        staticDatumName,
        marketValueLocalCcy,
      );
    },
    deps: [
      translations,
      form.watch(formFields.n1_static_datum_name)?.asset_class_2,
      form.watch(formFields.n1_portfolio_name)?.account_type,
      form.watch(formFields.n1_static_datum_name)?.text,
      form.watch(formFields.market_value_local_ccy),
    ],
  });

  return category_options.options as TSections;
}

/**
 * Hook to manage category auto-selection logic
 */
export function useCategoryAutoSelection({
  formFields,
  form,
  translations,
  keysAllowNullValue = [],
  isLoadingData = false,
}: {
  formFields: FormFields;
  form: UseFormReturn<FieldValues>;
  translations?: TTranslation;
  keysAllowNullValue?: string[];
  isLoadingData: boolean;
}) {
  const { isCategoryDisabled } = useCategoryFieldState(
    formFields,
    form,
    isLoadingData,
  );
  const movementsSections: TSections = getMovementsSections(
    formFields,
    form,
    translations,
  );

  useAutoSelectSingleOption(
    formFields,
    movementsSections,
    form,
    isCategoryDisabled,
    translations,
    keysAllowNullValue,
  );

  return {
    movementsSections,
    isCategoryDisabled,
  };
}
